<script setup lang="ts">
import Icon from '../icon/icon.vue';
import { IconName, IconSize } from '../icon/icon.constants';
import { AlertType } from './alert.constants';

type Props = {
  /** Semantic status, selecting the container colour pair and the default glyph. */
  type?: AlertType;
  /** Bold lead line. Ignored when the default slot is used. */
  title?: string;
  /** Supporting copy under the title. Ignored when the default slot is used. */
  description?: string;
  /** Renders the leading status glyph for the type. */
  icon?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  type: AlertType.INFO,
});

const slots = defineSlots<{
  default?: () => unknown;
}>();

const typeClass: Record<AlertType, string> = {
  [AlertType.INFO]:
    'bg-info-container border-on-info-container text-on-info-container',
  [AlertType.SUCCESS]:
    'bg-success-container border-on-success-container text-on-success-container',
  [AlertType.WARNING]:
    'bg-warning-container border-on-warning-container text-on-warning-container',
  [AlertType.ERROR]:
    'bg-error-container border-on-error-container text-on-error-container',
};

const glyph: Record<AlertType, IconName> = {
  [AlertType.INFO]: IconName.INFO_CIRCLE,
  [AlertType.SUCCESS]: IconName.CHECK_CIRCLE,
  [AlertType.WARNING]: IconName.WARNING,
  [AlertType.ERROR]: IconName.ERROR_CIRCLE,
};
</script>

<template>
  <div
    class="flex items-center gap-xl rounded-md border px-xl py-lg"
    :class="typeClass[props.type]"
  >
    <Icon v-if="props.icon" :name="glyph[props.type]" :size="IconSize.LG" />
    <div class="min-w-0 flex-1">
      <slot>
        <p v-if="props.title" class="text-label">{{ props.title }}</p>
        <p v-if="props.description" class="text-body-md">
          {{ props.description }}
        </p>
      </slot>
    </div>
  </div>
</template>
